import type { TraceData } from '@w/tel'

export interface TimelineBar {
  /** Start relative to the earliest known start in the trace. */
  startMs: number
  offsetPct: number
  widthPct: number
}

/**
 * Waterfall geometry for one trace. A span has known timing only with a finite end `timestamp`
 * and a finite non-negative `duration` (start = timestamp - duration); others get no bar.
 * Returns undefined when no span in the trace has known timing.
 */
export function traceTimeline(root: TraceData): { startMs: number; totalMs: number; bar(id: string): TimelineBar | undefined } | undefined {
  const known = new Map<string, { start: number; end: number }>()
  const visit = (node: TraceData) => {
    const { timestamp, duration } = node
    if (timestamp !== undefined && duration !== undefined && Number.isFinite(timestamp) && Number.isFinite(duration) && duration >= 0)
      known.set(node.id, { start: timestamp - duration, end: timestamp })
    for (const child of node.children) visit(child)
  }
  visit(root)
  if (known.size === 0) return undefined
  const spans = [...known.values()]
  const start = Math.min(...spans.map(span => span.start))
  const totalMs = Math.max(...spans.map(span => span.end)) - start
  const pct = (ms: number) => totalMs === 0 ? 0 : Math.min(100, Math.max(0, (ms / totalMs) * 100))
  return {
    /** Absolute epoch ms of the earliest known span start. */
    startMs: start,
    totalMs,
    bar(id) {
      const span = known.get(id)
      return span && { startMs: span.start - start, offsetPct: pct(span.start - start), widthPct: pct(span.end - span.start) }
    },
  }
}
